export type CompletionTopic = "purpose" | "habit" | "resilience" | "independence" | "future";

export const COMPLETION_ACKNOWLEDGEMENTS = [
  "Сегодняшняя работа сделана",
  "Набор пройден полностью",
  "План на сегодня закрыт",
  "Все задачи набора решены",
  "Сегодняшний шаг завершён",
  "Дневной цикл пройден",
  "Работа на сегодня завершена",
  "Набор доведён до конца",
  "Сегодняшняя дистанция пройдена",
  "Все задачи на сегодня закрыты",
  "Практика на сегодня завершена",
  "План выполнен без остатка",
  "Сегодняшний объём выполнен",
  "Ещё один день работы закрыт",
  "Задачи на сегодня закончены",
  "Дневной набор собран полностью",
  "Сегодня всё по плану",
  "Работа завершена до последней задачи",
  "Набор закрыт результатом",
  "Сегодняшний рубеж пройден",
  "Все пункты дня выполнены",
  "На сегодня задача выполнена",
  "Полный набор остался позади",
  "Сегодняшняя работа зафиксирована",
] as const;

/**
 * Сто простых ответов на вопросы «зачем мне математика?» и «зачем снова садиться?».
 * Текст рассчитан на четырнадцатилетнего ученика: одна понятная мысль без лекции.
 */
export const COMPLETION_REFLECTIONS: Record<CompletionTopic, readonly string[]> = {
  purpose: [
    "Математика учит отделять известные факты от предположений. Это помогает принимать решения без лишней уверенности.",
    "Каждая решённая задача делает следующую немного легче. Так постепенно появляется уверенность в своих силах.",
    "Математика тренирует умение не сдаваться, когда ответ не виден сразу. Этот навык пригодится в любой сложной работе.",
    "Необязательно любить каждую тему. Достаточно сделать сегодняшний шаг — результат складывается именно из таких шагов.",
    "Когда занимаешься понемногу, математика перестаёт выглядеть огромной и страшной. Она превращается в набор понятных задач.",
    "Математика учит разбивать большую проблему на маленькие части. После этого даже сложная задача становится решаемой.",
    "ИИ умеет быстро выдавать ответы, но иногда ошибается. Математика помогает заметить ошибку и не поверить уверенной ерунде.",
    "В мире ИИ особенно ценится человек, который умеет правильно поставить задачу и проверить результат. Математика тренирует оба навыка.",
    "Сегодня не нужно стать гением математики. Нужно только стать немного сильнее, чем вчера.",
    "Ошибиться в задаче — не значит плохо знать математику. Ошибка показывает, что именно нужно понять следующим.",
    "Регулярные короткие занятия сильнее редких больших рывков. Они превращают усилие в привычку.",
    "Когда решаешь задачу сам, ты тренируешь не только математику. Ты учишься доверять собственному мышлению.",
    "Математика помогает понимать проценты, скидки, кредиты и вероятность. Чем лучше считаешь, тем труднее тебя обмануть.",
    "Сложная задача кажется невозможной только до первого понятного шага. Найди его — дальше станет проще.",
    "Умение сосредоточиться на задаче становится редким и ценным. Каждое занятие тренирует это умение.",
    "Не всё должно получаться с первого раза. Настоящий навык появляется, когда возвращаешься и пробуешь ещё раз.",
    "Математика учит проверять слова числами. Это помогает отличать реальные факты от красивых обещаний.",
    "Даже пятнадцать минут осмысленной работы двигают вперёд. Главное — не идеальное занятие, а сделанный шаг.",
    "Чем больше задач ты разобрал, тем меньше ситуаций могут поставить тебя в тупик. Опыт постепенно превращается в спокойствие.",
    "Сегодняшняя работа нужна не только ради экзамена. Она тренирует способность разбираться в том, что сначала кажется сложным.",
  ],
  habit: [
    "Не нужно хотеть заниматься весь вечер. Достаточно сесть и спокойно сделать небольшой кусок.",
    "Даже десять минут работы возвращают ощущение, что ты управляешь делом, а не оно тобой.",
    "Навык растёт не сразу заметно. Но после нескольких занятий то, что тормозило, уже не требует столько сил.",
    "Начать снова стоит ради момента, когда знакомый тип задачи перестаёт пугать.",
    "Математика похожа на тренировку: небольшая нагрузка сегодня сохраняет форму на завтра.",
    "Необязательно ждать настроения. Иногда оно появляется уже после того, как начал.",
    "Один решённый пример — маленький результат, но именно из таких результатов складывается уверенность.",
    "Если сегодня сил мало, можно сделать меньше. Важно не бросать дело только потому, что день трудный.",
    "Занятие не обязано быть долгим, чтобы быть полезным. Важно, чтобы голова действительно поработала.",
    "Возвращаться легче, когда не требуешь от себя идеального старта. Открой задачу и дай себе несколько минут.",
    "То, что пока получается медленно, со временем может стать обычным действием.",
    "Маленькая работа сегодня уменьшает груз, который пришлось бы поднимать позже.",
    "Привычка садиться за дело освобождает от долгих споров с собой.",
    "Ошибка не отменяет работу. Она показывает место, где способ решения ещё нужно укрепить.",
    "Если ответ не сошёлся, это не тупик. Можно найти момент, где рассуждение свернуло не туда.",
    "Трудная задача полезна тем, что учит не уходить при первой неудаче.",
    "Иногда понимание приходит после нескольких попыток. Это нормальная часть работы, а не признак неспособности.",
    "Неудачное решение тоже даёт информацию: теперь ты знаешь, какой путь не сработал.",
    "Сесть снова стоит хотя бы для того, чтобы вчерашняя ошибка перестала быть загадкой.",
    "Умение спокойно исправляться пригодится чаще, чем умение всё делать без промаха.",
  ],
  resilience: [
    "Когда застрял, не нужно злиться на себя. Лучше проверить условие, записи и каждое действие по порядку.",
    "Сложность задачи ничего не говорит о тебе целиком. Она говорит только о том, что этот навык ещё растёт.",
    "Можно устать и всё равно продвинуться немного. Настойчивость не требует быть сильным каждую минуту.",
    "Попытка, проверка и исправление — обычный путь к точному ответу.",
    "Не все занятия будут лёгкими. Но именно на неудобных местах появляется настоящая устойчивость.",
    "Ошибку можно разобрать и оставить в прошлом. Неразобранная ошибка обычно встречается снова.",
    "Математика помогает сравнивать цены, тарифы и условия, а не выбирать наугад.",
    "Расчёты нужны, когда планируешь время, поездку, покупку или ремонт.",
    "Умение оценить результат помогает заметить, когда число выглядит неправдоподобно.",
    "Математика позволяет понять, сколько ты действительно платишь по рассрочке или кредиту.",
    "Масштаб, расстояние и скорость встречаются не только в задачнике. Они нужны в картах, дороге и технике.",
    "Когда умеешь считать, проще отличить выгодное предложение от красиво оформленного.",
    "Вероятность помогает трезво смотреть на риск, случайность и громкие обещания.",
    "Таблицы и графики используют в новостях, спорте и приложениях. Математика помогает читать их без догадок.",
    "Расчёт даёт возможность заранее проверить план, не тратя деньги и время на пробу.",
    "Математика полезна там, где нужно выбрать между несколькими вариантами и увидеть последствия.",
    "Умение прикинуть порядок числа защищает от дорогих ошибок в быту.",
    "Формулы — короткий способ описать связь между величинами. Такие связи встречаются в технике, играх и природе.",
    "Сесть за задачу стоит ради навыка разбираться самому, когда рядом нет готовой инструкции.",
    "Математика учит отличать доказательство от уверенного заявления.",
  ],
  independence: [
    "Решая задачи, ты тренируешь внимание к условиям. Это помогает не соглашаться на то, чего на самом деле не предлагали.",
    "Точное рассуждение позволяет объяснить своё решение так, чтобы его можно было проверить.",
    "Независимость начинается с умения самому понять, какие данные важны, а какие только отвлекают.",
    "Математика учит задавать полезный вопрос: откуда это следует.",
    "Когда умеешь рассуждать, сложнее навязать тебе чужой вывод без оснований.",
    "Решение задач развивает терпение к неизвестному. Не всё обязано быть ясным в первую минуту.",
    "Проверять свои предположения полезно в любом деле, где цена ошибки выше нескольких минут.",
    "Математика даёт способ спорить не громче, а точнее.",
    "Умение искать закономерности помогает быстрее разбираться в новых правилах и системах.",
    "Своя логика надёжнее чужой уверенности. Занятия дают ей тренировку.",
    "Когда можешь проверить ход мысли, ты меньше зависишь от готовых ответов.",
    "ИИ умеет быстро считать и писать код, но человеку всё равно нужно понимать, что именно ему поручить.",
    "В будущем ценнее будет не повторять действия, а ставить точные задачи и проверять результат.",
    "Математика нужна в программировании, инженерии, экономике, аналитике и многих новых профессиях.",
    "Технологии меняются быстро, а умение рассуждать и работать с числами остаётся полезным.",
    "Чем лучше ты понимаешь логику, тем проще использовать умные инструменты, а не просто нажимать кнопки.",
    "Данные влияют на решения компаний, врачей и городов. Чтобы работать с ними, нужна математическая основа.",
    "Автоматизация убирает простые действия, но оставляет человеку выбор способа и оценку последствий.",
    "Сесть снова стоит, чтобы будущие возможности не закрылись из-за одного трудного школьного предмета.",
    "Даже если профессия ещё не выбрана, математика сохраняет больше направлений открытыми.",
  ],
  future: [
    "Работа с моделями, роботами и приложениями начинается с понимания чисел, связей и правил.",
    "Умение объяснить машине задачу начинается с умения точно сформулировать её для себя.",
    "Технологии дают инструменты. Математическое мышление помогает выбрать, когда и как ими пользоваться.",
    "Сильная основа позволяет быстрее осваивать то, чего сегодня ещё даже нет.",
    "Экзамен становится спокойнее, когда знакомы не ответы, а способы действий.",
    "Сесть сегодня стоит ради того, чтобы на экзамене не тратить силы на панику перед привычной задачей.",
    "Уверенность появляется, когда ты уже встречал трудные места и знаешь, что с ними делать.",
    "Подготовка нужна не для идеального результата, а чтобы получить больше задач, которые ты умеешь довести до конца.",
    "На экзамене помогает не вдохновение, а ясный порядок действий, отработанный заранее.",
    "Чем чаще ты решаешь в спокойной обстановке, тем меньше нового остаётся для важного дня.",
    "Одна неудачная задача на экзамене не должна ломать всю работу. Этому тоже можно научиться заранее.",
    "Проверка решения экономит баллы, время и нервы. Этот навык появляется только в практике.",
    "Подготовка даёт право рассчитывать не на удачу, а на собственные действия.",
    "Если сейчас закрыть один слабый навык, на экзамене станет на одну причину для тревоги меньше.",
    "Полезно заранее узнать, где ты обычно спешишь, путаешь знак или неверно читаешь условие.",
    "Экзаменационная задача выглядит короче, когда ты узнаёшь знакомую идею за непривычными словами.",
    "Возвращаться к занятиям стоит ради спокойного ощущения: я уже делал похожее и знаю, с чего начать.",
    "Не нужно уметь решить всё. Нужно надёжно делать то, чему ты успел научиться.",
    "Важный день легче выдержать, если до него было достаточно обычных рабочих дней.",
    "Подготовка не убирает все трудности, но делает их знакомыми и управляемыми.",
  ],
};

export const COMPLETION_REFLECTION_LINES: readonly string[] = [
  ...COMPLETION_REFLECTIONS.purpose,
  ...COMPLETION_REFLECTIONS.habit,
  ...COMPLETION_REFLECTIONS.resilience,
  ...COMPLETION_REFLECTIONS.independence,
  ...COMPLETION_REFLECTIONS.future,
];

export type CompletionCopy = {
  acknowledgement: string;
  reflection: string;
};

function stableHash(value: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

/** Одна и та же пара для одного ученика и календарного дня, без хранения. */
export function completionCopy(learnerKey: string, date: string): CompletionCopy {
  const acknowledgementIndex =
    stableHash(`ack\0${learnerKey}\0${date}`) % COMPLETION_ACKNOWLEDGEMENTS.length;
  const reflectionIndex =
    stableHash(`reflection\0${learnerKey}\0${date}`) % COMPLETION_REFLECTION_LINES.length;

  return {
    acknowledgement: COMPLETION_ACKNOWLEDGEMENTS[acknowledgementIndex]!,
    reflection: COMPLETION_REFLECTION_LINES[reflectionIndex]!,
  };
}
